/**
 * nova-expression.tsx — an expression or persona tile, animated.
 * Ambient loops (float, blink, gallop…) are the canonical nova.css keyframes,
 * keyed off the `expr-<id>` wrapper class; framer-motion handles the stateful
 * layer: entrance pop and cross-fade when the expression changes.
 */
import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import { cn } from "@/lib/utils";
import { NovaAvatar } from "./nova-avatar";
import { NOVA_EXPRESSIONS, type NovaExpressionId } from "./nova-expressions";
import { NOVA_PERSONAS, type NovaPersonaId } from "./personas";
import "./nova.css";

export type NovaMoodId = NovaExpressionId | NovaPersonaId;

export interface NovaExpressionProps {
  id: NovaMoodId;
  /** Rendered width in px. */
  size?: number;
  /** Spring pop on mount / expression change. Default true. */
  pop?: boolean;
  className?: string;
}

/**
 * NOVA-1 — expression transitions are a soft crossfade instead of a
 * hard swap.  Two layers cross over 320ms (`easeInOut`) with a tiny
 * scale nudge so the change reads as a mood shift rather than a
 * teleport.  Respects `prefers-reduced-motion` by dropping the
 * scale/y transform and running a plain opacity fade.
 */
export function NovaExpression({ id, size = 240, pop = true, className }: NovaExpressionProps) {
  const persona = (NOVA_PERSONAS as Record<string, React.ComponentType<{ className?: string }> | undefined>)[id];
  const reduce = useReducedMotion();

  return (
    <AnimatePresence mode="popLayout" initial={pop}>
      <motion.div
        key={id}
        className={cn(`expr-${id}`, "inline-block leading-none", className)}
        style={{ width: size }}
        initial={
          pop
            ? reduce
              ? { opacity: 0 }
              : { opacity: 0, scale: 0.94, y: 4 }
            : false
        }
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
        transition={
          reduce
            ? { duration: 0.16 }
            : { type: "spring", stiffness: 320, damping: 28, mass: 0.6 }
        }
      >
        {persona
          ? React.createElement(persona, { className: "w-full h-auto" })
          : <NovaAvatar width={size} {...NOVA_EXPRESSIONS[id as NovaExpressionId].opts} className="w-full h-auto" />}
      </motion.div>
    </AnimatePresence>
  );
}
